import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Badge,
  Button,
  Combobox,
  type ComboboxOption,
  ConfirmDialog,
  Table,
  TableWrap,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { TextLink } from '@/components/ui/TextLink';
import { api } from '@/lib/api';
import { formatDateTime } from '@/lib/format';
import type { Company, FabLinkSuggestion, FabSchoolLinkRow } from '@/lib/types';
import {
  PiCardField,
  PiCardList,
  PiEmpty,
  PiError,
  PiLoading,
  PiSearch,
  PiSortHeader,
  PiTabs,
  PiToolbar,
  piCount,
} from './pi-kit';

/**
 * SEED FAB school ↔ SEED EMERGE company links — a review queue (owner 2026-10-18).
 *
 * Every FAB client school in the cycle, its confirmed company (if any) and the
 * server's suggestions. Links are STAFF-CONFIRMED: nothing here links on its
 * own — "Link", the company picker and the bulk action are deliberate writes.
 *
 * ⚠️ One school → one company. The PUT is an upsert keyed on the school.
 *
 * ⚠️ Writes are per row and independent. The bulk action runs them ONE AT A
 * TIME (no bulk endpoint), and a failure is reported on THAT row while every
 * other row keeps its result. Partial success is normal and the summary says
 * which.
 *
 * Tabs live in the URL under `links` (values `unlinked` | `linked` | `all`;
 * never rename). ABSENT = the smart default: Needs linking while any school is
 * unlinked, otherwise All. Choosing the tab that equals the default deletes the
 * key rather than writing it. The search box is local state on purpose: the
 * page already owns `q` for its own tables.
 *
 * "Domain match" for the bulk action: the row's suggestions name exactly ONE
 * company by domain. Two domain matches for one school is ambiguous and is left
 * for a person — never resolved by order.
 */

type LinkFilter = 'unlinked' | 'linked' | 'all';

const LINKS_KEY = 'links';
const COMPANY_PAGE_SIZE = 100;
const PAGE_STEP = 50;
/** A stable empty list, so the memos do not recompute while loading. */
const NO_ROWS: readonly FabSchoolLinkRow[] = [];

function readLinkFilter(value: string | null): LinkFilter | null {
  return value === 'unlinked' || value === 'linked' || value === 'all' ? value : null;
}

function errorMessage(error: unknown, fallback: string): string {
  return error instanceof Error && error.message.trim() !== '' ? error.message : fallback;
}

const rankOf = (s: FabLinkSuggestion) => (s.reason === 'domain' ? 2 : 0) + (s.countryMatch ? 1 : 0);

/** Suggestions best first (domain, then name; a country match first within a kind), one per company. */
function rankedSuggestions(row: FabSchoolLinkRow): FabLinkSuggestion[] {
  const best = new Map<number, FabLinkSuggestion>();
  for (const suggestion of row.suggestions) {
    const seen = best.get(suggestion.company.id);
    if (seen === undefined || rankOf(suggestion) > rankOf(seen)) best.set(suggestion.company.id, suggestion);
  }
  return [...best.values()].sort((a, b) => rankOf(b) - rankOf(a));
}

/** The single unambiguous domain match, or null. */
function domainMatchOf(row: FabSchoolLinkRow): FabLinkSuggestion | null {
  const domain = row.suggestions.filter((s) => s.reason === 'domain');
  const ids = new Set(domain.map((s) => s.company.id));
  return ids.size === 1 ? (domain[0] ?? null) : null;
}

function schoolFacts(row: FabSchoolLinkRow): string {
  return [row.domain, row.country].filter((part): part is string => part !== null && part !== '').join(' · ');
}

function matchesSearch(row: FabSchoolLinkRow, term: string): boolean {
  if (term === '') return true;
  const haystack = [row.name, row.shortName, row.domain, row.country, row.company?.name]
    .filter((part): part is string => typeof part === 'string')
    .join(' ')
    .toLowerCase();
  return haystack.includes(term);
}

type Load =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; rows: FabSchoolLinkRow[] };

interface BulkPlanItem {
  row: FabSchoolLinkRow;
  suggestion: FabLinkSuggestion;
}

interface BulkSummary {
  attempted: number;
  linked: number;
  failed: { name: string }[];
}

export interface FabSchoolLinksPanelProps {
  cycleId: number | null;
  canEdit: boolean;
}

export function FabSchoolLinksPanel({ cycleId, canEdit }: FabSchoolLinksPanelProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlFilter = readLinkFilter(searchParams.get(LINKS_KEY));

  const [reloadToken, setReloadToken] = useState(0);
  const requestKey = `${cycleId ?? ''}#${reloadToken}`;
  const [fetched, setFetched] = useState<{ key: string; load: Load } | null>(null);
  const load: Load = fetched !== null && fetched.key === requestKey ? fetched.load : { status: 'loading' };

  /** schoolId → the company id being written for it. */
  const [busy, setBusy] = useState<Readonly<Record<string, number>>>({});
  const [rowErrors, setRowErrors] = useState<Readonly<Record<string, string>>>({});
  const [choosing, setChoosing] = useState<string | null>(null);
  const [unlinkTarget, setUnlinkTarget] = useState<FabSchoolLinkRow | null>(null);
  const [unlinkBusy, setUnlinkBusy] = useState(false);
  const [unlinkError, setUnlinkError] = useState<string | null>(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkProgress, setBulkProgress] = useState(0);
  const [bulkSummary, setBulkSummary] = useState<BulkSummary | null>(null);

  const [search, setSearch] = useState('');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [limit, setLimit] = useState(PAGE_STEP);

  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;
    api
      .listFabSchoolLinks(cycleId ?? undefined, signal)
      .then((rows) => {
        if (signal.aborted) return;
        setRowErrors({});
        setBulkSummary(null);
        setFetched({ key: requestKey, load: { status: 'ready', rows } });
      })
      .catch((error: unknown) => {
        if (signal.aborted) return;
        setFetched({
          key: requestKey,
          load: { status: 'error', message: errorMessage(error, 'Funnel Analytics & BI schools could not be loaded.') },
        });
      });
    return () => controller.abort();
  }, [cycleId, requestKey]);

  /*
   * The company picker searches server-side (the directory is longer than one
   * page). ONE search shared by every row: only one picker is open at a time,
   * and nothing is fetched until somebody opens one.
   */
  const pickerOpen = canEdit && choosing !== null;
  const [companySearch, setCompanySearch] = useState('');
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loadedSearch, setLoadedSearch] = useState<string | null>(null);

  useEffect(() => {
    if (!pickerOpen) return;
    const controller = new AbortController();
    api
      .listCompanies(
        { search: companySearch, perPage: COMPANY_PAGE_SIZE, sort: 'name', order: 'asc' },
        controller.signal,
      )
      .then((result) => {
        if (!controller.signal.aborted) setCompanies(result.data);
      })
      .catch(() => {
        if (!controller.signal.aborted) setCompanies([]);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoadedSearch(companySearch);
      });
    return () => controller.abort();
  }, [pickerOpen, companySearch]);

  const companiesLoading = pickerOpen && loadedSearch !== companySearch;
  const searchCompanies = useCallback((term: string) => setCompanySearch(term), []);
  const companyOptions = useMemo<ComboboxOption[]>(
    () => companies.map((company) => ({ value: String(company.id), label: company.name })),
    [companies],
  );

  const ready = load.status === 'ready';
  const rows = load.status === 'ready' ? load.rows : NO_ROWS;
  const linkedCount = rows.filter((row) => row.company !== null).length;
  const unlinkedCount = rows.length - linkedCount;
  const smartDefault: LinkFilter = ready && unlinkedCount === 0 ? 'all' : 'unlinked';
  const filter: LinkFilter = urlFilter ?? smartDefault;
  const term = search.trim().toLowerCase();

  const visible = useMemo(() => {
    const kept = rows.filter(
      (row) =>
        (filter === 'all' ? true : filter === 'linked' ? row.company !== null : row.company === null) &&
        matchesSearch(row, term),
    );
    const sorted = [...kept].sort((a, b) => a.name.localeCompare(b.name));
    return sortDir === 'asc' ? sorted : sorted.reverse();
  }, [rows, filter, term, sortDir]);
  const shown = visible.slice(0, limit);

  const bulkPlan = useMemo<BulkPlanItem[]>(
    () =>
      rows.flatMap((row) => {
        if (row.company !== null) return [];
        const suggestion = domainMatchOf(row);
        return suggestion === null ? [] : [{ row, suggestion }];
      }),
    [rows],
  );

  const setFilter = (next: LinkFilter) => {
    setLimit(PAGE_STEP);
    setChoosing(null);
    setSearchParams(
      (current) => {
        const params = new URLSearchParams(current);
        if (next === smartDefault) params.delete(LINKS_KEY);
        else params.set(LINKS_KEY, next);
        return params;
      },
      { replace: true },
    );
  };

  const changeSearch = (value: string) => {
    setSearch(value);
    setLimit(PAGE_STEP);
  };

  const replaceRow = (next: FabSchoolLinkRow) => {
    setFetched((current) =>
      current !== null && current.load.status === 'ready'
        ? {
            key: current.key,
            load: {
              status: 'ready',
              rows: current.load.rows.map((row) => (row.schoolId === next.schoolId ? next : row)),
            },
          }
        : current,
    );
  };

  const markBusy = (schoolId: string, companyId: number | null) => {
    setBusy((current) => {
      const next = { ...current };
      if (companyId === null) delete next[schoolId];
      else next[schoolId] = companyId;
      return next;
    });
  };

  const setRowError = (schoolId: string, message: string | null) => {
    setRowErrors((current) => {
      const next = { ...current };
      if (message === null) delete next[schoolId];
      else next[schoolId] = message;
      return next;
    });
  };

  /** One write. Resolves true when the link landed. */
  const writeLink = async (row: FabSchoolLinkRow, companyId: number): Promise<boolean> => {
    markBusy(row.schoolId, companyId);
    setRowError(row.schoolId, null);
    try {
      const saved = await api.setFabSchoolLink(row.schoolId, companyId);
      if (mounted.current) replaceRow(saved);
      return true;
    } catch (error: unknown) {
      if (mounted.current) setRowError(row.schoolId, errorMessage(error, 'The link could not be saved.'));
      return false;
    } finally {
      if (mounted.current) markBusy(row.schoolId, null);
    }
  };

  const linkSchool = (row: FabSchoolLinkRow, companyId: number) => {
    if (bulkRunning || busy[row.schoolId] !== undefined || row.company?.id === companyId) return;
    setChoosing(null);
    void writeLink(row, companyId);
  };

  const runBulk = async () => {
    const plan = bulkPlan;
    setBulkOpen(false);
    setBulkRunning(true);
    setBulkProgress(0);
    setBulkSummary(null);
    const failed: BulkSummary['failed'] = [];
    let linked = 0;
    for (const [index, item] of plan.entries()) {
      if (!mounted.current) return;
      const ok = await writeLink(item.row, item.suggestion.company.id);
      if (ok) linked += 1;
      else failed.push({ name: item.row.name });
      if (mounted.current) setBulkProgress(index + 1);
    }
    if (!mounted.current) return;
    setBulkRunning(false);
    setBulkSummary({ attempted: plan.length, linked, failed });
  };

  const confirmUnlink = () => {
    const row = unlinkTarget;
    if (row === null) return;
    setUnlinkBusy(true);
    setUnlinkError(null);
    api
      .deleteFabSchoolLink(row.schoolId)
      .then(() => {
        if (!mounted.current) return;
        replaceRow({ ...row, company: null, linkedAt: null, linkedBy: null });
        setRowError(row.schoolId, null);
        setUnlinkTarget(null);
      })
      .catch((error: unknown) => {
        if (mounted.current) setUnlinkError(errorMessage(error, 'The link could not be removed.'));
      })
      .finally(() => {
        if (mounted.current) setUnlinkBusy(false);
      });
  };

  const tabs = [
    { key: 'unlinked', label: 'Needs linking', count: ready ? unlinkedCount : null },
    { key: 'linked', label: 'Linked', count: ready ? linkedCount : null },
    { key: 'all', label: 'All', count: ready ? rows.length : null },
  ] as const;

  const linkedShare = rows.length === 0 ? 0 : Math.round((linkedCount / rows.length) * 100);

  /* ── Cells, shared by the table and the cards so both say the same thing ── */

  const schoolCell = (row: FabSchoolLinkRow) => {
    const facts = schoolFacts(row);
    return (
      <div className="min-w-0">
        <div className="font-medium break-words text-fg">{row.name}</div>
        {facts !== '' && <div className="text-xs break-words text-fg-muted">{facts}</div>}
      </div>
    );
  };

  /** Linked: the company and who linked it. Unlinked: every suggestion with its own Link. */
  const companyCell = (row: FabSchoolLinkRow) => {
    if (row.company !== null) {
      const byline = [row.linkedBy?.name, row.linkedAt !== null ? formatDateTime(row.linkedAt) : null]
        .filter((part): part is string => typeof part === 'string' && part !== '')
        .join(' · ');
      return (
        <div className="min-w-0">
          <TextLink to={`/companies/${row.company.id}`} className="font-medium">
            {row.company.name}
          </TextLink>
          {byline !== '' && <div className="text-xs text-fg-muted">Linked by {byline}</div>}
        </div>
      );
    }
    const suggestions = rankedSuggestions(row);
    if (suggestions.length === 0) return <span className="text-fg-muted">No suggestion</span>;
    const rowBusy = bulkRunning || busy[row.schoolId] !== undefined;
    return (
      <ul className="m-0 grid list-none gap-2 p-0">
        {suggestions.map((suggestion) => (
          <li key={suggestion.company.id} className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <TextLink to={`/companies/${suggestion.company.id}`} className="min-w-0 break-words">
              {suggestion.company.name}
            </TextLink>
            <Badge tone={suggestion.reason === 'domain' ? 'success' : 'neutral'} variant="soft">
              {suggestion.reason === 'domain' ? 'Domain match' : 'Name match'}
            </Badge>
            {suggestion.countryMatch && (
              <Badge tone="info" variant="outline">
                Same country
              </Badge>
            )}
            {canEdit && (
              <Button
                size="sm"
                variant={suggestion === suggestions[0] ? 'primary' : 'secondary'}
                className="max-sm:h-11"
                loading={busy[row.schoolId] === suggestion.company.id}
                disabled={rowBusy}
                aria-label={`Link ${row.name} to ${suggestion.company.name}`}
                onClick={() => linkSchool(row, suggestion.company.id)}
              >
                Link
              </Button>
            )}
          </li>
        ))}
      </ul>
    );
  };

  const actionsCell = (row: FabSchoolLinkRow, layout: 'card' | 'table') => {
    if (!canEdit) {
      return row.company !== null ? (
        <Badge tone="success" variant="soft">
          Linked
        </Badge>
      ) : (
        <Badge tone="neutral" variant="soft">
          Not linked
        </Badge>
      );
    }
    const rowBusy = bulkRunning || busy[row.schoolId] !== undefined;
    if (row.company !== null) {
      return (
        <Button
          size="sm"
          variant="ghost"
          className="max-sm:h-11"
          disabled={rowBusy}
          aria-label={`Unlink ${row.name}`}
          onClick={() => {
            setUnlinkError(null);
            setUnlinkTarget(row);
          }}
        >
          Unlink
        </Button>
      );
    }
    const hasSuggestion = row.suggestions.length > 0;
    const labelId = `fab-link-${layout}-${row.schoolId}`;
    if (choosing !== row.schoolId) {
      return (
        <Button
          size="sm"
          variant="secondary"
          className="max-sm:h-11"
          disabled={rowBusy}
          onClick={() => setChoosing(row.schoolId)}
        >
          {hasSuggestion ? 'Choose another' : 'Choose a company'}
        </Button>
      );
    }
    return (
      <div className="flex flex-wrap items-center gap-2">
        <div className="w-64 max-w-full">
          <span id={labelId} className="sr-only">
            {`Company for ${row.name}`}
          </span>
          <Combobox
            aria-describedby={labelId}
            value=""
            onChange={(value) => {
              const id = Number.parseInt(value, 10);
              if (Number.isInteger(id) && id > 0) linkSchool(row, id);
            }}
            options={companyOptions}
            placeholder="Company"
            searchPlaceholder="Search companies…"
            onSearch={searchCompanies}
            loading={companiesLoading}
            disabled={rowBusy}
          />
        </div>
        <Button
          size="sm"
          variant="ghost"
          className="max-sm:h-11"
          disabled={rowBusy}
          onClick={() => setChoosing(null)}
        >
          Cancel
        </Button>
      </div>
    );
  };

  const rowError = (row: FabSchoolLinkRow) => {
    const message = rowErrors[row.schoolId];
    return message === undefined ? null : (
      <p role="alert" className="m-0 mt-1 text-xs text-danger">
        {message}
      </p>
    );
  };

  const emptyState = () => {
    if (rows.length === 0) {
      return <PiEmpty title="No Funnel Analytics & BI client schools in this cycle." description="Choose another cycle." />;
    }
    if (term !== '') {
      return (
        <PiEmpty
          title={`No schools match “${search.trim()}”.`}
          action={
            <Button size="sm" variant="secondary" onClick={() => changeSearch('')}>
              Clear search
            </Button>
          }
        />
      );
    }
    if (filter === 'linked') {
      return (
        <PiEmpty
          title="No linked schools yet."
          action={
            <Button size="sm" variant="secondary" onClick={() => setFilter('unlinked')}>
              Show schools that need linking
            </Button>
          }
        />
      );
    }
    return (
      <PiEmpty
        title="Every school is linked."
        action={
          <Button size="sm" variant="secondary" onClick={() => setFilter('linked')}>
            Show linked schools
          </Button>
        }
      />
    );
  };

  const showBulk = canEdit && ready && bulkPlan.length > 0;

  return (
    <div className="grid min-w-0 gap-3">
      <PiToolbar
        trailing={
          showBulk ? (
            <Button
              size="sm"
              variant="secondary"
              className="max-sm:h-11"
              loading={bulkRunning}
              disabled={bulkRunning}
              onClick={() => setBulkOpen(true)}
            >
              {bulkRunning
                ? `Linking ${piCount(bulkProgress)} of ${piCount(bulkPlan.length)}`
                : `Link all domain matches (${piCount(bulkPlan.length)})`}
            </Button>
          ) : undefined
        }
      >
        <PiSearch value={search} onChange={changeSearch} label="Search schools" className="w-full sm:w-72" />
        {ready && rows.length > 0 && (
          <div className="flex min-w-[10rem] items-center gap-2 text-sm text-fg">
            <span className="whitespace-nowrap">
              <span className="font-medium tabular-nums">{piCount(linkedCount)}</span>
              {' of '}
              <span className="tabular-nums">{piCount(rows.length)}</span>
              {' linked'}
            </span>
            <div aria-hidden="true" className="h-1.5 w-24 overflow-hidden rounded-full bg-surface-sunken">
              <div className="h-full rounded-full bg-success" style={{ width: `${linkedShare}%` }} />
            </div>
          </div>
        )}
      </PiToolbar>

      <PiTabs tabs={tabs} value={filter} onChange={setFilter} ariaLabel="Link status" />

      {bulkSummary !== null && (
        <Notice
          tone={bulkSummary.failed.length === 0 ? 'success' : bulkSummary.linked === 0 ? 'danger' : 'warning'}
          role="status"
          action={
            <Button size="sm" variant="ghost" onClick={() => setBulkSummary(null)}>
              Dismiss
            </Button>
          }
        >
          {bulkSummary.failed.length === 0
            ? `Linked ${piCount(bulkSummary.linked)} of ${piCount(bulkSummary.attempted)} schools.`
            : `Linked ${piCount(bulkSummary.linked)} of ${piCount(bulkSummary.attempted)} schools. Not linked: ${bulkSummary.failed
                .map((item) => item.name)
                .join(', ')}.`}
        </Notice>
      )}

      {load.status === 'error' ? (
        <PiError message={load.message} onRetry={() => setReloadToken((n) => n + 1)} />
      ) : load.status === 'loading' ? (
        <PiLoading variant="table" rows={6} />
      ) : visible.length === 0 ? (
        emptyState()
      ) : (
        <>
          <PiCardList
            items={shown}
            getKey={(row) => row.schoolId}
            render={(row) => (
              <div className="grid min-w-0 gap-2">
                {schoolCell(row)}
                <PiCardField label={row.company !== null ? 'Company' : 'Suggested'} value={companyCell(row)} />
                <div>
                  {actionsCell(row, 'card')}
                  {rowError(row)}
                </div>
              </div>
            )}
          />

          <div className="hidden md:block">
            <TableWrap frozenHeader>
              <Table>
                <THead>
                  <TR>
                    <TH frozen="left">
                      <PiSortHeader
                        label="School"
                        active
                        direction={sortDir}
                        onClick={() => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))}
                      />
                    </TH>
                    <TH>{filter === 'unlinked' ? 'Suggested companies' : 'Company'}</TH>
                    <TH>{canEdit ? <span className="sr-only">Actions</span> : 'Status'}</TH>
                  </TR>
                </THead>
                <TBody>
                  {shown.map((row) => (
                    <TR key={row.schoolId}>
                      <TD frozen="left" verticalAlign="top" className="min-w-[14rem]">
                        {schoolCell(row)}
                      </TD>
                      <TD verticalAlign="top" className="min-w-[18rem]">
                        {companyCell(row)}
                      </TD>
                      <TD verticalAlign="top">
                        {actionsCell(row, 'table')}
                        {rowError(row)}
                      </TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            </TableWrap>
          </div>

          {visible.length > shown.length && (
            <div className="flex flex-wrap items-center justify-center gap-3 text-sm text-fg-muted">
              <span className="tabular-nums">{`${piCount(shown.length)} of ${piCount(visible.length)}`}</span>
              <Button size="sm" variant="secondary" className="max-sm:h-11" onClick={() => setLimit((n) => n + PAGE_STEP)}>
                Show more
              </Button>
            </div>
          )}
        </>
      )}

      <ConfirmDialog
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        onConfirm={() => void runBulk()}
        title="Link all domain matches"
        tone="primary"
        confirmLabel={`Link ${piCount(bulkPlan.length)}`}
        description={
          <div className="space-y-2">
            <p className="m-0">
              {`Link ${piCount(bulkPlan.length)} ${bulkPlan.length === 1 ? 'school' : 'schools'} to the company whose domain matches?`}
            </p>
            <ul className="m-0 max-h-64 list-none space-y-1 overflow-y-auto p-0 text-sm">
              {bulkPlan.map((item) => (
                <li key={item.row.schoolId} className="flex flex-wrap gap-x-1.5">
                  <span className="text-fg">{item.row.name}</span>
                  <span aria-hidden="true" className="text-fg-muted">
                    →
                  </span>
                  <span className="sr-only">to</span>
                  <span className="font-medium text-fg">{item.suggestion.company.name}</span>
                </li>
              ))}
            </ul>
          </div>
        }
      />

      <ConfirmDialog
        open={unlinkTarget !== null}
        onClose={() => {
          if (!unlinkBusy) setUnlinkTarget(null);
        }}
        onConfirm={confirmUnlink}
        title="Unlink school"
        description={
          unlinkTarget !== null && unlinkTarget.company !== null ? (
            <>
              Unlink <strong>{unlinkTarget.name}</strong> from <strong>{unlinkTarget.company.name}</strong>? Its SEED
              FAB figures stop showing on that company.
            </>
          ) : null
        }
        confirmLabel="Unlink"
        tone="danger"
        loading={unlinkBusy}
        error={unlinkError}
      />
    </div>
  );
}
